import type { AppShellPanelDefaults } from '@efficiently/client-ui-app-shell';

/**
 * What each section seeds the shell's shared `(open, activeButtonId)` pair with the FIRST time it
 * is entered in a session (BR-4). Every later entry into an already-visited section leaves the pair
 * alone, which is what makes the user's own choice travel with them between sections.
 *
 * The keys are the section ids `APP_SECTIONS` already uses, and the values are plain literals for
 * the same reason that table's are: this file is the data crossing the `type:core` → `type:ui` seam
 * (TR-1), and a data table is where a literal belongs. Two agreements it is on the hook for, and
 * neither has a compiler behind it — the keys must be section ids from `APP_SECTIONS`, and
 * `buttonId` must be an id from `APP_RAIL_BUTTONS`.
 *
 * Every default names the button it opens on, because "the panel is open" and "exactly one control
 * is expanded" are one fact (BR-2): a bare `open: true` would put the very first render on screen
 * open with nothing active, and the placeholder would be reading an `absence` off nothing.
 *
 * The Control Center and Design defaults name `menu` rather than `ai-chat`, because `menu` is the rail button each section
 * registers its navigation tree against — Clarity its domain tree, Design its domains-and-projects
 * menu (EFF-26168) — and that tree is what a reader entering the section used to see without asking.
 * Seeding a panel on a button whose feature has not landed would have made the move a regression: the
 * same reader would arrive at a placeholder and have to find the tree themselves.
 *
 * Supply names `menu` for the same reason Control Center does, not by copying it: "the panel is
 * open" and "exactly one control is expanded" are one fact (BR-2), so a bare `open: true` would put
 * its first render on screen open with nothing active, and `menu` is the button a reader entering a
 * section reaches for first.
 *
 * A default belongs here for every section that CAN be enabled, not only for the ones the base
 * `APP_SECTIONS` table ships enabled — a section's `enabled` flag is what decides whether it is
 * reachable, and that decision is not this table's to anticipate. The seed is read the first time a
 * section is entered and nothing checks that a key exists for it: a section with no default arrives
 * with the panel closed and is recorded as visited regardless, so the omission never raises
 * anything. It is spent silently, on the one entry that cannot be replayed. This file is reached only
 * from the build that HAS the Control Center — the plain one registers no defaults at all, having no
 * panel.
 */
export const PANEL_DEFAULTS: AppShellPanelDefaults = {
	'control-center': { open: true, buttonId: 'menu' },
	design: { open: true, buttonId: 'menu' },
	supply: { open: true, buttonId: 'menu' },
};
